import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, CheckCircle, Trash2, Printer, Search, Mail, Edit2, Download, DollarSign, Wrench, Shield, RefreshCw } from 'lucide-react';
import Modal from '../components/Modal';
import { invoicesService, tenantsService } from '../services/supabaseService';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useSessionStorage('invoices_searchTerm', '');
  const [isModalOpen, setIsModalOpen] = useSessionStorage('invoices_isModalOpen', false);
  const [editingInvoice, setEditingInvoice] = useSessionStorage('invoices_editingInvoice', null);
  const [exportMonth, setExportMonth] = useSessionStorage('invoices_exportMonth', '');

  // Form State
  const [formData, setFormData] = useSessionStorage('invoices_formData', {
    receipt_no: '',
    name: '',
    email: '',
    aadhaar: '',
    gender: 'Female',
    room_no: '',
    room_type: '2 Sharing',
    security_deposit: '',
    monthly_rent: '',
    maintenance_fees: '',
    payment_date: '',
    payment_month: '',
    transaction_id: 'CASH',
    status: 'Paid',
    mobile: '',
    notes: '',
    selected_tenant_id: ''
  });

  const isFirstMonth = (paymentDate, joinDate) => {
    if (!paymentDate || !joinDate) return false;
    const pDate = new Date(paymentDate);
    const jDate = new Date(joinDate);
    const diffTime = pDate.getTime() - jDate.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    // Allow a window around the join date for first month billing
    return diffDays >= -15 && diffDays <= 35;
  };

  const fetchData = async () => {
    try {
      const invData = await invoicesService.getAll();
      setInvoices(Array.isArray(invData) ? invData : []);

      const tenData = await tenantsService.getAll();
      setTenants(Array.isArray(tenData) ? tenData : []);
    } catch (err) {
      console.error('Error fetching data from Supabase:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExport = () => {
    const headers = [
      'Receipt No', 'Resident Name', 'Room No', 'Room Type', 'Monthly Rent', 
      'Security Deposit', 'Maintenance Fee', 'Payment Date', 'Billing Month', 
      'Txn ID', 'Status', 'Mobile', 'Email', 'Aadhaar', 'Notes'
    ];
    let toExport = filteredInvoices;
    if (exportMonth) {
      toExport = filteredInvoices.filter(inv => {
        const pMonth = inv.payment_date ? inv.payment_date.substring(0, 7) : '';
        return pMonth === exportMonth;
      });
    }

    const rows = [headers];
    toExport.forEach(inv => {
      const matchingTenant = tenants.find(t => (t.name || '').toLowerCase() === (inv.name || '').toLowerCase() && t.room_no === inv.room_no);
      const tenantJoinDate = matchingTenant ? matchingTenant.date_of_joining : inv.created_at;
      const showSecAndMaint = isFirstMonth(inv.payment_date, tenantJoinDate);

      rows.push([
        inv.receipt_no,
        `"${inv.name}"`,
        inv.room_no,
        inv.room_type,
        inv.monthly_rent,
        showSecAndMaint ? (inv.security_deposit || 0) : 0,
        showSecAndMaint ? (inv.maintenance_fees || 0) : 0,
        inv.payment_date ? inv.payment_date.split('T')[0] : '',
        inv.payment_month,
        inv.transaction_id,
        inv.status,
        inv.mobile,
        inv.email || '',
        inv.aadhaar || '',
        `"${(inv.notes || '').replace(/"/g, '""')}"`
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Invoices_Export_${exportMonth || 'All'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenModal = (invoice = null) => {
    if (invoice && invoice.id) {
      setEditingInvoice(invoice);
      setFormData({
        receipt_no: invoice.receipt_no || '',
        name: invoice.name || '',
        email: invoice.email || '',
        aadhaar: invoice.aadhaar || '',
        gender: invoice.gender || 'Female',
        room_no: invoice.room_no || '',
        room_type: invoice.room_type || '2 Sharing',
        security_deposit: invoice.security_deposit || '',
        monthly_rent: invoice.monthly_rent || '',
        maintenance_fees: invoice.maintenance_fees || '',
        payment_date: invoice.payment_date ? invoice.payment_date.split('T')[0] : '',
        payment_month: invoice.payment_month || '',
        transaction_id: invoice.transaction_id || 'CASH',
        status: invoice.status || 'Paid',
        mobile: invoice.mobile || '',
        notes: invoice.notes || '',
        selected_tenant_id: ''
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      const currentMonthStr = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
      const randomReceipt = `REC-${Date.now().toString().slice(-6)}`;

      setEditingInvoice(null);
      setFormData({
        receipt_no: randomReceipt,
        name: '',
        email: '',
        aadhaar: '',
        gender: 'Female',
        room_no: '',
        room_type: '2 Sharing',
        security_deposit: '',
        monthly_rent: '',
        maintenance_fees: '',
        payment_date: today,
        payment_month: currentMonthStr,
        transaction_id: 'CASH',
        status: 'Paid',
        mobile: '',
        notes: 'Monthly rental & maintenance payment',
        selected_tenant_id: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleTenantChange = (e) => {
    const tId = e.target.value;
    const selectedTenant = tenants.find(t => t.id.toString() === tId);
    if (selectedTenant) {
      setFormData({
        ...formData,
        selected_tenant_id: tId,
        name: selectedTenant.name || '',
        email: selectedTenant.email || '',
        aadhaar: selectedTenant.aadhaar || '',
        mobile: selectedTenant.mobile || '',
        room_no: selectedTenant.room_no || '',
        room_type: selectedTenant.room_type || '2 Sharing',
        monthly_rent: selectedTenant.monthly_rent || 0,
        security_deposit: selectedTenant.security_deposit || 0,
        maintenance_fees: selectedTenant.maintenance_fees || 0
      });
    } else {
      setFormData({
        ...formData,
        selected_tenant_id: tId,
        name: '', email: '', aadhaar: '', mobile: '', room_no: '', monthly_rent: '', security_deposit: '', maintenance_fees: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.room_no) {
      alert("Please provide resident name and room number.");
      return;
    }

    try {
      if (editingInvoice) {
        await invoicesService.update(editingInvoice.id, formData);
      } else {
        await invoicesService.create(formData);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error saving invoice:', err);
      alert('Error saving invoice: ' + err.message);
    }
  };

  const handleMarkPaid = async (id, currentInv) => {
    try {
      await invoicesService.update(id, { ...currentInv, status: 'Paid' });
      fetchData();
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert('Error updating invoice: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice/receipt?')) {
      try {
        await invoicesService.delete(id);
        fetchData();
      } catch (err) {
        console.error('Error deleting invoice:', err);
        alert('Error deleting invoice: ' + err.message);
      }
    }
  };

  const handlePrint = (invoice) => {
    // Find matching tenant to check join date
    const matchingTenant = tenants.find(t => (t.name || '').toLowerCase() === (invoice.name || '').toLowerCase() && t.room_no === invoice.room_no);
    const tenantJoinDate = matchingTenant ? matchingTenant.date_of_joining : invoice.created_at;
    const showSecAndMaint = isFirstMonth(invoice.payment_date, tenantJoinDate);

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt ${invoice.receipt_no}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #222; }
            .header-box { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #8b5cf6; padding-bottom: 20px; margin-bottom: 25px; }
            .logo { background: #f3f4f6; color: #8b5cf6; font-weight: bold; padding: 15px 25px; border-radius: 8px; font-size: 22px; display: inline-block; }
            .section { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 25px; border: 1px solid #e2e8f0; }
            .table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .table th { background: #f1f5f9; padding: 12px 15px; text-align: left; border-bottom: 2px solid #cbd5e1; font-weight: 600; }
            .table td { padding: 10px 15px; border-bottom: 1px solid #e2e8f0; }
            .text-right { text-align: right; }
            .total-row { font-size: 20px; font-weight: bold; border-top: 2px solid #334155; padding-top: 15px; }
            .rules { margin-top: 30px; font-size: 13px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 20px; }
          </style>
        </head>
        <body style="margin: 0; padding: 25px; display: flex; justify-content: center;">
          <div style="width: 100%; max-width: 850px; border: 1px solid #cbd5e1; padding: 30px; border-radius: 12px; box-sizing: border-box; background: #fff;">
            <div class="header-box">
              <div>
                <div class="logo">FernPetals PG</div>
                <h2 style="color: #1e293b; margin: 15px 0 5px 0;">FernPetals Ladies PG</h2>
                <p style="color: #64748b; margin: 0; font-size: 14px; max-width: 400px;">H No 41 Khata No 179, Akshaya Nilaya, Near RNSIT College, Rajarajeshwari Nagar, Bengaluru - 560098</p>
              </div>
              <div class="text-right">
                <h1 style="color: #8b5cf6; margin: 0; font-size: 32px;">RENT RECEIPT</h1>
                <p style="margin: 8px 0 4px 0; font-size: 16px;"><strong>Receipt No:</strong> ${invoice.receipt_no}</p>
                <p style="margin: 4px 0; font-size: 16px;"><strong>Date:</strong> ${(invoice.payment_date || '').split('T')[0]}</p>
                <p style="margin: 4px 0; font-size: 16px;"><strong>Billing Month:</strong> ${invoice.payment_month}</p>
                <p style="margin: 4px 0;"><strong>Status:</strong> <span style="color: #10b981; font-weight: bold;">${invoice.status.toUpperCase()}</span></p>
              </div>
            </div>

            <div class="section">
              <h3 style="margin-top: 0; color: #334155; font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px;">Received From:</h3>
              <p style="margin: 6px 0; font-size: 22px; color: #0f172a;"><strong>${invoice.name}</strong></p>
              <div style="display: flex; gap: 30px; color: #475569; margin-top: 6px; font-size: 15px;">
                <div><strong>Room No:</strong> ${invoice.room_no} (${invoice.room_type})</div>
                <div><strong>Mobile:</strong> ${invoice.mobile}</div>
                <div><strong>Aadhaar:</strong> ${invoice.aadhaar || 'N/A'}</div>
              </div>
            </div>

            <table class="table">
              <thead>
                <tr>
                  <th>Payment Description</th>
                  <th class="text-right">Amount (INR)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Monthly Rent (${invoice.payment_month})</td>
                  <td class="text-right">₹${invoice.monthly_rent}</td>
                </tr>
                ${showSecAndMaint ? `
                <tr>
                  <td>Security Deposit (Refundable)</td>
                  <td class="text-right">₹${invoice.security_deposit || 0}</td>
                </tr>
                <tr>
                  <td>Maintenance Fee (First Month)</td>
                  <td class="text-right">₹${invoice.maintenance_fees || 0}</td>
                </tr>
                ` : ''}
              </tbody>
            </table>

            <div style="display: flex; justify-content: flex-end; margin-bottom: 10px;">
              <div style="width: 350px;">
                <div class="total-row" style="display: flex; justify-content: space-between;">
                  <span>Total Paid:</span>
                  <span style="color: #8b5cf6;">₹${Number(invoice.monthly_rent) + (showSecAndMaint ? (Number(invoice.security_deposit || 0) + Number(invoice.maintenance_fees || 0)) : 0)}</span>
                </div>
                <p class="text-right" style="margin-top: 4px; color: #475569; font-size: 15px;"><strong>Payment Mode / Txn ID:</strong> ${invoice.transaction_id}</p>
                ${invoice.notes ? `<p class="text-right" style="margin-top: 4px; color: #64748b; font-size: 14px;"><em>Note: ${invoice.notes}</em></p>` : ''}
              </div>
            </div>

            <div class="rules">
              <h4 style="color: #334155; margin-bottom: 8px; font-size: 14px;">Terms, Rules & Information:</h4>
              <ul style="padding-left: 20px; line-height: 1.7;">
                <li>Rent must be paid strictly on or before the 5th of every calendar month.</li>
                <li>Security deposit will be refunded solely at the time of vacating, subject to providing a mandatory 30-day prior written notice.</li>
                <li>Maintenance fees and monthly rentals once paid are non-refundable.</li>
                <li>This is an electronically generated rental payment receipt and does not require a physical signature.</li>
              </ul>
              <p style="text-align: center; margin-top: 5px; font-weight: bold; color: #0f172a; font-size: 15px;">Thank you for choosing FernPetals Ladies PG.</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSendEmail = async (id) => {
    try {
      const res = await fetch(`http://localhost:5000/api/invoices/${id}/send`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert('Receipt/Invoice successfully emailed to the resident!');
        fetchData();
      } else {
        alert('Failed to send invoice email.');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      alert('Error connecting to the mail server.');
    }
  };

  const [selectedMonthFilter, setSelectedMonthFilter] = useSessionStorage('invoices_selectedMonthFilter', 'All');

  const monthCounts = invoices.reduce((acc, inv) => {
    const m = inv.payment_month;
    if (m) {
      acc[m] = (acc[m] || 0) + 1;
    }
    return acc;
  }, {});

  const availableMonths = ['All', ...Object.keys(monthCounts).sort()];

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = (inv.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.room_no || '').toString().includes(searchTerm) ||
      (inv.receipt_no || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = selectedMonthFilter === 'All' || inv.payment_month === selectedMonthFilter;
    return matchesSearch && matchesMonth;
  });

  const [currentPage, setCurrentPage] = useSessionStorage('invoices_currentPage', 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonthFilter, setCurrentPage]);

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage) || 1;
  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const selectedTenantForSummary = formData.selected_tenant_id ? tenants.find(t => t.id.toString() === formData.selected_tenant_id.toString()) : null;

  const getBillingMonthStr = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const cardsInvoices = invoices.filter(inv => {
    return selectedMonthFilter === 'All' || inv.payment_month === selectedMonthFilter;
  });

  const paidCardsInvoices = cardsInvoices.filter(inv => inv.status === 'Paid');

  const rentTotal = paidCardsInvoices.reduce((sum, inv) => sum + Number(inv.monthly_rent || 0), 0);

  const secDepositTotal = paidCardsInvoices.reduce((sum, inv) => {
    const matchingTenant = tenants.find(t => (t.name || '').toLowerCase() === (inv.name || '').toLowerCase() && t.room_no === inv.room_no);
    const tenantJoinDate = matchingTenant ? matchingTenant.date_of_joining : inv.created_at;
    const showSecAndMaint = isFirstMonth(inv.payment_date, tenantJoinDate);
    return sum + (showSecAndMaint ? Number(inv.security_deposit || 0) : 0);
  }, 0);

  const maintFeeTotal = paidCardsInvoices.reduce((sum, inv) => {
    const matchingTenant = tenants.find(t => (t.name || '').toLowerCase() === (inv.name || '').toLowerCase() && t.room_no === inv.room_no);
    const tenantJoinDate = matchingTenant ? matchingTenant.date_of_joining : inv.created_at;
    const showSecAndMaint = isFirstMonth(inv.payment_date, tenantJoinDate);
    return sum + (showSecAndMaint ? Number(inv.maintenance_fees || 0) : 0);
  }, 0);

  const reversedDepositTotal = tenants.reduce((sum, tenant) => {
    if (tenant.reversed_deposit && tenant.vacated) {
      const tenantMonth = getBillingMonthStr(tenant.vacated_date);
      if (selectedMonthFilter === 'All' || tenantMonth === selectedMonthFilter) {
        return sum + Number(tenant.security_deposit || 0);
      }
    }
    return sum;
  }, 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <h2>Invoice & Receipt Management</h2>
          <p>Generate rental receipts, track billing history, and email invoices directly.</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="month"
            value={exportMonth}
            onChange={(e) => setExportMonth(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            title="Select month for export"
          />
          <button className="btn-primary flex items-center gap-2" onClick={handleExport} style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-primary)' }}>
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Generate Receipt
          </button>
        </div>
      </div>

      {/* KPI Cards showing monthly financial metrics */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DollarSign size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Rent Collected</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{rentTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Wrench size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Maint. Fees</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{maintFeeTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(236, 72, 153, 0.1)', color: '#ec4899', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Sec. Deposits</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{secDepositTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Reversed Sec. Deposits</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{reversedDepositTotal.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
          <div className="relative flex-1" style={{ minWidth: '280px', maxWidth: '400px' }}>
            <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Search by resident, room or receipt..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', width: '100%' }}
            />
          </div>
          <div className="flex items-center gap-3">
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: '500' }}>
              Showing {filteredInvoices.length} of {invoices.length} receipts
            </span>
            <select
              value={selectedMonthFilter}
              onChange={(e) => setSelectedMonthFilter(e.target.value)}
              style={{ padding: '0.75rem 1.25rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', minWidth: '220px' }}
            >
              {availableMonths.map(m => (
                <option key={m} value={m} style={{ color: '#000' }}>
                  {m === 'All' ? `All Billing Months (${invoices.length})` : `${m} (${monthCounts[m] || 0})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Receipt No</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Resident Details</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Room & Type</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Monthly Rent (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Sec. Deposit (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Maint. Fee (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Billing Month</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Txn Mode</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((inv) => {
                const matchingTenant = tenants.find(t => (t.name || '').toLowerCase() === (inv.name || '').toLowerCase() && t.room_no === inv.room_no);
                const tenantJoinDate = matchingTenant ? matchingTenant.date_of_joining : inv.created_at;
                const showSecAndMaint = isFirstMonth(inv.payment_date, tenantJoinDate);

                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem 0', fontWeight: '600', color: 'var(--primary-color)' }}>{inv.receipt_no}</td>
                    <td style={{ padding: '1rem 0' }}>
                      <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{inv.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        {inv.mobile} {inv.email ? `| ${inv.email}` : ''}
                      </div>
                      {inv.mail_sent ? (
                        <div style={{ fontSize: '0.7rem', color: 'var(--accent-color)', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <CheckCircle size={10} /> Mailed {inv.mail_sent_at ? new Date(inv.mail_sent_at).toLocaleDateString() : ''}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '1rem 0' }}>
                      <div style={{ fontWeight: '600' }}>Room {inv.room_no}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{inv.room_type}</div>
                    </td>
                    <td style={{ padding: '1rem 0', fontWeight: '600' }}>{inv.monthly_rent}</td>
                    <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>{showSecAndMaint ? (inv.security_deposit || 0) : 0}</td>
                    <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>{showSecAndMaint ? (inv.maintenance_fees || 0) : 0}</td>
                    <td style={{ padding: '1rem 0' }}>
                      <div style={{ fontWeight: '500' }}>{inv.payment_month}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Paid: {(inv.payment_date || '').split('T')[0]}</div>
                    </td>
                    <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>
                      <span style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '0.25rem' }}>{inv.transaction_id}</span>
                    </td>
                    <td style={{ padding: '1rem 0' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: inv.status === 'Paid' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: inv.status === 'Paid' ? 'var(--accent-color)' : 'var(--danger-color)'
                      }}>
                        {inv.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem 0', textAlign: 'right' }}>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleSendEmail(inv.id)} title="Email Receipt to Resident" style={{ padding: '0.5rem', background: 'transparent', color: 'var(--primary-color)' }}>
                          <Mail size={16} />
                        </button>
                        <button onClick={() => handleOpenModal(inv)} title="Edit Details" style={{ padding: '0.5rem', background: 'transparent', color: 'var(--text-secondary)' }}>
                          <Edit2 size={16} />
                        </button>
                        {inv.status !== 'Paid' && (
                          <button onClick={() => handleMarkPaid(inv.id, inv)} title="Mark as Paid" style={{ padding: '0.5rem', background: 'transparent', color: 'var(--accent-color)' }}>
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button onClick={() => handlePrint(inv)} title="Print / PDF Receipt" style={{ padding: '0.5rem', background: 'transparent', color: 'var(--text-secondary)' }}>
                          <Printer size={16} />
                        </button>
                        <button onClick={() => handleDelete(inv.id)} title="Delete Record" style={{ padding: '0.5rem', background: 'transparent', color: 'var(--danger-color)' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredInvoices.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No billing receipts generated yet.
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length} entries
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingInvoice ? "Edit Receipt Details" : "Generate Rental Receipt"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {!editingInvoice && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Select Active Resident (Auto-Fills Details)</label>
              <select
                value={formData.selected_tenant_id}
                onChange={handleTenantChange}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="" disabled style={{ color: '#000' }}>Choose active resident...</option>
                {tenants.filter(t => !t.vacated).map(t => (
                  <option key={t.id} value={t.id} style={{ color: '#000' }}>{t.name} (Room {t.room_no})</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Receipt No</label>
              <input required type="text" value={formData.receipt_no} onChange={e => setFormData({ ...formData, receipt_no: e.target.value })} placeholder="REC-123456" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Resident Full Name</label>
              <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Aarohi Sharma" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Room No</label>
              <input required type="text" value={formData.room_no} onChange={e => setFormData({ ...formData, room_no: e.target.value })} placeholder="e.g. 101" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Room Type</label>
              <select
                value={formData.room_type}
                onChange={e => setFormData({ ...formData, room_type: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Single" style={{ color: '#000' }}>Single Occupancy</option>
                <option value="2 Sharing" style={{ color: '#000' }}>2 Sharing</option>
                <option value="3 Sharing" style={{ color: '#000' }}>3 Sharing</option>
                <option value="4 Sharing" style={{ color: '#000' }}>4 Sharing</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Gender</label>
              <select
                value={formData.gender}
                onChange={e => setFormData({ ...formData, gender: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Female" style={{ color: '#000' }}>Female</option>
                <option value="Male" style={{ color: '#000' }}>Male</option>
                <option value="Other" style={{ color: '#000' }}>Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Email</label>
              <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="tenant@example.com" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Mobile No</label>
              <input required type="text" value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} placeholder="9876543210" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Monthly Rent (₹)</label>
              <input required type="number" value={formData.monthly_rent} onChange={e => setFormData({ ...formData, monthly_rent: e.target.value })} placeholder="8000" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Security Deposit (₹)</label>
              <input type="number" value={formData.security_deposit} onChange={e => setFormData({ ...formData, security_deposit: e.target.value })} placeholder="5000" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Maintenance Fee (₹)</label>
              <input type="number" value={formData.maintenance_fees} onChange={e => setFormData({ ...formData, maintenance_fees: e.target.value })} placeholder="1000" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Payment Date</label>
              <input required type="date" value={formData.payment_date} onChange={e => setFormData({ ...formData, payment_date: e.target.value })} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Billing Month</label>
              <input required type="text" value={formData.payment_month} onChange={e => setFormData({ ...formData, payment_month: e.target.value })} placeholder="e.g. May 2026" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Transaction ID / Payment Mode</label>
              <input required type="text" value={formData.transaction_id} onChange={e => setFormData({ ...formData, transaction_id: e.target.value })} placeholder="e.g. UPI / TXN12345" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Payment Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Paid" style={{ color: '#000' }}>Paid</option>
                <option value="Unpaid" style={{ color: '#000' }}>Unpaid</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Payment Notes / Remarks</label>
            <input type="text" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="e.g. Initial advance / Monthly dues" />
          </div>

          {selectedTenantForSummary && (() => {
            const showSummaryExtras = isFirstMonth(formData.payment_date, selectedTenantForSummary.date_of_joining);
            return (
              <div style={{ padding: '1.25rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem', border: '1px solid var(--border-color)', marginTop: '0.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>Receipt Billing Calculation Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <div>Monthly Rent:</div>
                  <div style={{ textAlign: 'right', fontWeight: '500', color: 'var(--text-primary)' }}>₹{formData.monthly_rent || 0}</div>
                  {showSummaryExtras ? (
                    <>
                      <div>Security Deposit (First Month):</div>
                      <div style={{ textAlign: 'right', fontWeight: '500', color: 'var(--text-primary)' }}>₹{formData.security_deposit || 0}</div>
                      <div>Maintenance Fee (First Month):</div>
                      <div style={{ textAlign: 'right', fontWeight: '500', color: 'var(--text-primary)' }}>₹{formData.maintenance_fees || 0}</div>
                    </>
                  ) : (
                    <div style={{ gridColumn: 'span 2', fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem', marginBottom: '0.25rem' }}>
                      * Security Deposit & Maintenance only applicable during first month of joining ({selectedTenantForSummary.date_of_joining ? selectedTenantForSummary.date_of_joining.split('T')[0] : 'N/A'}).
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem', fontWeight: 'bold', fontSize: '1rem', color: 'var(--text-primary)' }}>Total Received:</div>
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem', fontWeight: 'bold', fontSize: '1rem', textAlign: 'right', color: 'var(--accent-color)' }}>
                    ₹{Number(formData.monthly_rent || 0) + (showSummaryExtras ? (Number(formData.security_deposit || 0) + Number(formData.maintenance_fees || 0)) : 0)}
                  </div>
                </div>
              </div>
            );
          })()}

          <button type="submit" className="btn-primary mt-4">
            {editingInvoice ? 'Update Receipt Record' : 'Create Rental Receipt'}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default Invoices;
